import { useState } from "react";
import { MedsSVG } from "@/components/we360/SVGIllustrations";
import { PillSelect } from "@/components/we360/PillSelect";
import { AutocompleteInput } from "@/components/we360/AutocompleteInput";
import { MEDICAMENTOS_LIST } from "@/data/medicamentos";
import { X, Pill, AlertTriangle } from "lucide-react";

function DoseSelector({ med, onConfirm, onCancel }) {
  const [selectedDose, setSelectedDose] = useState("");
  const [customDose, setCustomDose] = useState("");
  const showCustom = selectedDose === "Otra";

  const confirm = () => {
    const dose = showCustom ? customDose.trim() : selectedDose;
    if (!dose) return;
    onConfirm(dose);
  };

  return (
    <div
      className="p-5 rounded-3xl mt-4 mb-4 text-left border border-white/60 shadow-md backdrop-blur-md"
      style={{ background: "rgba(255, 255, 255, 0.6)" }}
      data-testid="dose-selector"
    >
      <p className="text-xs font-bold uppercase tracking-wider text-[#00628F] mb-3">
        Dosis de {med.nombre}
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {(med.dosisComunes || []).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => { setSelectedDose(d); setCustomDose(""); }}
            className="px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all hover:-translate-y-0.5"
            style={{
              background: selectedDose === d ? "#00628F" : "rgba(255, 255, 255, 0.8)",
              color: selectedDose === d ? "white" : "#2D2A24",
              border: `1px solid ${selectedDose === d ? "#00628F" : "rgba(0, 98, 143, 0.15)"}`,
              boxShadow: selectedDose === d ? "0 4px 12px rgba(0, 98, 143, 0.15)" : "none",
            }}
            data-testid={`dose-${d}`}
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setSelectedDose("Otra")}
          className="px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all hover:-translate-y-0.5"
          style={{
            background: showCustom ? "#00628F" : "rgba(255, 255, 255, 0.8)",
            color: showCustom ? "white" : "#2D2A24",
            border: `1px solid ${showCustom ? "#00628F" : "rgba(0, 98, 143, 0.15)"}`,
            boxShadow: showCustom ? "0 4px 12px rgba(0, 98, 143, 0.15)" : "none",
          }}
          data-testid="dose-custom"
        >
          Otra
        </button>
      </div>
      {showCustom && (
        <input
          type="text"
          value={customDose}
          onChange={(e) => setCustomDose(e.target.value)}
          placeholder="Escribe la dosis (ej: 5mg, 850mg)"
          className="w-full px-4 py-3 rounded-2xl text-xs outline-none mb-4 transition-all border border-gray-200 focus:border-[#00628F] focus:ring-1 focus:ring-[#00628F]"
          style={{ background: "#FCFAEE", color: "#2D2A24" }}
          autoFocus
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), confirm())}
          data-testid="dose-custom-input"
        />
      )}
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={confirm}
          disabled={showCustom ? !customDose.trim() : !selectedDose}
          className="flex-1 py-3 rounded-2xl text-xs font-bold uppercase tracking-wider cursor-pointer transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed text-white shadow-sm"
          style={{ background: "#00628F", border: "none" }}
          data-testid="dose-confirm"
        >
          Confirmar
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="py-3 px-5 rounded-2xl text-xs font-bold uppercase tracking-wider cursor-pointer transition-all border hover:bg-white/40 active:scale-[0.98]"
          style={{ background: "transparent", color: "#71644C", borderColor: "rgba(113, 100, 76, 0.3)" }}
          data-testid="dose-cancel"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function FreeDoseInput({ name, onConfirm, onCancel }) {
  const [dose, setDose] = useState("");
  return (
    <div
      className="p-5 rounded-3xl mt-4 mb-4 text-left border border-white/60 shadow-md backdrop-blur-md"
      style={{ background: "rgba(255, 255, 255, 0.6)" }}
      data-testid="free-dose"
    >
      <p className="text-xs font-bold uppercase tracking-wider text-[#00628F] mb-3">
        Dosis de "{name}"
      </p>
      <input
        type="text"
        value={dose}
        onChange={(e) => setDose(e.target.value)}
        placeholder="Dosis (ej: 5mg, 850mg)"
        className="w-full px-4 py-3 rounded-2xl text-xs outline-none mb-4 transition-all border border-gray-200 focus:border-[#00628F] focus:ring-1 focus:ring-[#00628F]"
        style={{ background: "#FCFAEE", color: "#2D2A24" }}
        autoFocus
        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), dose.trim() && onConfirm(dose.trim()))}
        data-testid="free-dose-input"
      />
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={() => dose.trim() && onConfirm(dose.trim())}
          disabled={!dose.trim()}
          className="flex-1 py-3 rounded-2xl text-xs font-bold uppercase tracking-wider cursor-pointer transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed text-white shadow-sm"
          style={{ background: "#00628F", border: "none" }}
          data-testid="free-dose-confirm"
        >
          Agregar
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="py-3 px-5 rounded-2xl text-xs font-bold uppercase tracking-wider cursor-pointer transition-all border hover:bg-white/40 active:scale-[0.98]"
          style={{ background: "transparent", color: "#71644C", borderColor: "rgba(113, 100, 76, 0.3)" }}
          data-testid="free-dose-cancel"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default function Step6Meds({ data, setField, errors }) {
  const medsError = errors.getError("medicamentos");
  const [pendingMed, setPendingMed] = useState(null); // curated med waiting for dose
  const [pendingFree, setPendingFree] = useState(null); // free text name waiting for dose
  const [confirmRemoveIdx, setConfirmRemoveIdx] = useState(null); // index waiting for confirmation to remove
  const [confirmClearAll, setConfirmClearAll] = useState(false); // confirmation when switching Sí -> No with existing meds

  // Parse legacy formats
  const meds = (() => {
    if (!data.medicamentos) return [];
    if (Array.isArray(data.medicamentos)) return data.medicamentos;
    if (typeof data.medicamentos === "string" && data.medicamentos.trim()) {
      return data.medicamentos.split(",").map((m) => ({
        nombre: m.trim(),
        dosis: "",
        codigoATC: null,
        source: "free_text",
      }));
    }
    return [];
  })();

  const addCuratedMed = (dose) => {
    if (!pendingMed) return;
    const entry = {
      nombre: pendingMed.nombre,
      codigoATC: pendingMed.codigoATC,
      dosis: dose,
      nombreComercial: pendingMed.nombreComercial || null,
      source: "curated",
    };
    setField("medicamentos", [...meds, entry]);
    setField("medicationsConfirmedEmpty", false);
    setPendingMed(null);
  };

  const addFreeMed = (dose) => {
    if (!pendingFree) return;
    const entry = {
      nombre: pendingFree,
      codigoATC: null,
      dosis: dose,
      nombreComercial: null,
      source: "free_text",
    };
    setField("medicamentos", [...meds, entry]);
    setField("medicationsConfirmedEmpty", false);
    setPendingFree(null);
  };

  const requestRemoveMed = (index) => {
    setConfirmRemoveIdx(index);
  };

  const confirmRemoveMed = () => {
    if (confirmRemoveIdx === null) return;
    const nextMeds = meds.filter((_, i) => i !== confirmRemoveIdx);
    setField("medicamentos", nextMeds);
    if (nextMeds.length === 0) {
      setField("medicationsConfirmedEmpty", true);
    }
    setConfirmRemoveIdx(null);
  };

  const handleUsaMedicamentosChange = (v) => {
    const normalized = v === "Sí" ? "Si" : v === "No" ? "No" : v;
    if (normalized === "No" && meds.length > 0) {
      setConfirmClearAll(true);
      return;
    }
    setConfirmClearAll(false);
    setField("usaMedicamentos", normalized);
  };

  const confirmClearAllMeds = () => {
    setField("medicamentos", []);
    setField("usaMedicamentos", "No");
    setField("medicationsConfirmedEmpty", true);
    setConfirmClearAll(false);
    setConfirmRemoveIdx(null);
  };

  const hasPending = pendingMed || pendingFree;

  const showMedsInput = data.usaMedicamentos === "Si" || data.usaMedicamentos === "Sí";
  const medToRemove = confirmRemoveIdx !== null ? meds[confirmRemoveIdx] : null;

  return (
    <div>
      <div className="text-center mb-4">
        <MedsSVG useMeds={data.usaMedicamentos} />
      </div>

      <PillSelect
        label="¿Usas medicamentos de forma regular?"
        options={["Sí", "No"]}
        value={data.usaMedicamentos === "Si" ? "Sí" : data.usaMedicamentos === "No" ? "No" : data.usaMedicamentos}
        onChange={handleUsaMedicamentosChange}
        error={errors.getError("usaMedicamentos")}
        testId="pill-medicamentos"
        columns={2}
      />

      {/* Confirmation dialog when switching from Sí -> No while having declared medications */}
      {confirmClearAll && meds.length > 0 && (
        <div
          className="p-4 rounded-3xl mt-4 text-left border shadow-sm transition-all"
          style={{
            background: "linear-gradient(135deg, #FFF8EC 0%, #FDF2DC 100%)",
            borderColor: "rgba(217, 119, 6, 0.35)",
          }}
          data-testid="meds-confirm-clear-all"
        >
          <div className="flex items-start gap-2.5 mb-3">
            <div
              className="w-7 h-7 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: "rgba(217, 119, 6, 0.15)" }}
            >
              <AlertTriangle size={15} color="#B45309" />
            </div>
            <div>
              <p className="text-xs font-extrabold uppercase tracking-wider" style={{ color: "#92400E" }}>
                Confirmación de retiro de medicamentos
              </p>
              <p className="text-xs mt-1 leading-relaxed" style={{ color: "#57534E" }}>
                Tenías <strong>{meds.length} {meds.length === 1 ? "medicamento registrado" : "medicamentos registrados"}</strong> en tu perfil ({meds.map((m) => m.nombre || m.name).join(", ")}). ¿Confirmas que ya no necesitas o ya no tomas estos medicamentos?
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={confirmClearAllMeds}
              className="flex-1 py-2.5 px-4 rounded-2xl text-xs font-bold uppercase tracking-wider text-white cursor-pointer transition-all hover:opacity-90"
              style={{ background: "#B45309", border: "none" }}
              data-testid="meds-confirm-clear-yes"
            >
              Sí, confirmo que ya no los necesito
            </button>
            <button
              type="button"
              onClick={() => setConfirmClearAll(false)}
              className="py-2.5 px-4 rounded-2xl text-xs font-bold uppercase tracking-wider cursor-pointer transition-all border hover:bg-white/60"
              style={{ background: "white", color: "#57534E", borderColor: "rgba(120, 113, 108, 0.3)" }}
              data-testid="meds-confirm-clear-cancel"
            >
              Conservar mis medicamentos
            </button>
          </div>
        </div>
      )}

      {showMedsInput && (
        <div className="glass-card p-5 rounded-3xl mt-5 text-left border border-white/40 shadow-sm">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#00628F] mb-4">
            Medicamentos Habituales
          </h3>

          <label className="block text-xs font-semibold mb-3 text-[#2D2A24]">
            ¿Cuáles medicamentos tomas regularmente?
          </label>

          {/* Existing medication chips */}
          {meds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="meds-chips">
              {meds.map((med, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 px-3 py-2 rounded-full transition-all hover:scale-[1.02]"
                  style={{
                    background: med.source === "curated"
                      ? "linear-gradient(135deg, #E8F4F8 0%, #D9EDF3 100%)"
                      : "rgba(245, 239, 226, 0.7)",
                    border: `1px solid ${med.source === "curated" ? "rgba(0,98,143,0.2)" : "rgba(226, 217, 203, 0.6)"}`,
                  }}
                  title={med.codigoATC ? `ATC: ${med.codigoATC}` : undefined}
                  data-testid={`med-tag-${idx}`}
                >
                  <Pill size={14} style={{ color: med.source === "curated" ? "#00628F" : "#71644C" }} />
                  <span className="text-xs font-semibold text-[#2D2A24]">{med.nombre || med.name}</span>
                  {(med.dosis || med.dosificacion) && (
                    <span className="text-xs text-[#71644C] font-medium">({med.dosis || med.dosificacion})</span>
                  )}
                  {med.source === "free_text" && (
                    <span className="text-[10px] text-[#B5A998] italic">(sin código)</span>
                  )}
                  <button
                    onClick={() => requestRemoveMed(idx)}
                    className="ml-1 p-0.5 rounded-full hover:bg-white/60 transition-colors cursor-pointer"
                    style={{ background: "transparent", border: "none" }}
                    type="button"
                    data-testid={`med-remove-${idx}`}
                  >
                    <X size={14} style={{ color: "#71644C" }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Individual medication removal confirmation */}
          {medToRemove && (
            <div
              className="p-4 rounded-2xl mb-4 border transition-all"
              style={{
                background: "linear-gradient(135deg, #FFF8EC 0%, #FDF2DC 100%)",
                borderColor: "rgba(217, 119, 6, 0.35)",
              }}
              data-testid="med-remove-confirm-box"
            >
              <div className="flex items-start gap-2.5 mb-2.5">
                <AlertTriangle size={16} color="#B45309" className="shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed" style={{ color: "#57534E" }}>
                  ¿Confirmas que ya no necesitas o ya no tomas <strong>{medToRemove.nombre || medToRemove.name}</strong>{(medToRemove.dosis || medToRemove.dosificacion) ? ` (${medToRemove.dosis || medToRemove.dosificacion})` : ""}?
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={confirmRemoveMed}
                  className="flex-1 py-2 px-3 rounded-xl text-xs font-bold uppercase tracking-wider text-white cursor-pointer transition-all hover:opacity-90"
                  style={{ background: "#B45309", border: "none" }}
                  data-testid="med-remove-confirm-yes"
                >
                  Sí, ya no lo necesito
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemoveIdx(null)}
                  className="py-2 px-4 rounded-xl text-xs font-bold uppercase tracking-wider cursor-pointer transition-all border hover:bg-white/60"
                  style={{ background: "white", color: "#57534E", borderColor: "rgba(120, 113, 108, 0.3)" }}
                  data-testid="med-remove-confirm-cancel"
                >
                  Conservar
                </button>
              </div>
            </div>
          )}

          {/* Dose selector for pending curated med */}
          {pendingMed && (
            <DoseSelector
              med={pendingMed}
              onConfirm={addCuratedMed}
              onCancel={() => setPendingMed(null)}
            />
          )}

          {/* Dose input for pending free text med */}
          {pendingFree && (
            <FreeDoseInput
              name={pendingFree}
              onConfirm={addFreeMed}
              onCancel={() => setPendingFree(null)}
            />
          )}

          {/* Autocomplete input (hidden while selecting dose) */}
          {!hasPending && (
            <div className="relative">
              <AutocompleteInput
                list={MEDICAMENTOS_LIST}
                displayField="nombre"
                subtextField="subgrupo"
                onSelect={(item) => setPendingMed(item)}
                onFreeText={(text) => setPendingFree(text)}
                placeholder="Escribe el nombre del medicamento..."
                allowFreeText={true}
                testId="autocomplete-meds"
              />
            </div>
          )}

          {medsError && (
            <p className="text-xs mt-3 font-semibold text-[#C0453A]" data-testid="meds-error">
              {medsError}
            </p>
          )}

          <p className="text-[11px] mt-3 leading-relaxed text-[#B5A998]">
            Ingresa el nombre comercial o principio activo del fármaco. Si no encuentras la coincidencia exacta, puedes agregarlo como texto libre presionando Enter.
          </p>
        </div>
      )}

      {data.usaMedicamentos === "No" && (
        <div
          className="text-center py-6 text-[13px] font-semibold mt-4 glass-card rounded-3xl border border-white/40"
          style={{ color: "#2A9D8F" }}
          data-testid="meds-no-message"
        >
          ¡Perfecto! Pasemos al último paso para finalizar.
        </div>
      )}
    </div>
  );
}

